import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, X, Pencil, Trash2, Plus, ArrowLeft, Minus } from "lucide-react";
import { CONFIG_SECTIONS, ConfigField } from "config/adminConfig";
import adminConfigService, { ConfigItem } from "services/AdminConfig/adminConfigService";
import HeaderButtons from "components/Button/HeaderButtons";
import InlineTable, { Spinner, fieldCls, TableColumn } from "components/InlineTable";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import LeaveRecipientConfig from "./components/LeaveRecipientConfig";

const COLUMNS: TableColumn[] = [
  { key: "name", header: "Name" },
  { key: "actions", header: "", width: 148 },
];

// Generic name/id CRUD editor. Rendered for every config field except those
// with a custom `variant` (see ConfigDetail wrapper below).
const GenericConfigDetail: React.FC<{ field: ConfigField }> = ({ field }) => {
  const navigate = useNavigate();

  const [items, setItems] = useState<ConfigItem[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  const [newNames, setNewNames] = useState<string[]>([]);
  const [newNameErrors, setNewNameErrors] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editError, setEditError] = useState("");
  const [updating, setUpdating] = useState<number | null>(null);

  const [deleting, setDeleting] = useState<number | null>(null);

  // Item pending delete confirmation (drives the confirm modal).
  const [deleteTarget, setDeleteTarget] = useState<ConfigItem | null>(null);

  const editInputRef = useRef<HTMLInputElement>(null);
  const lastNewInputRef = useRef<HTMLInputElement>(null);

  const isAnyBusy = adding || !!deleting || !!updating || pageLoading;

  useEffect(() => {
    if (newNames.length > 0) {
      lastNewInputRef.current?.focus();
      lastNewInputRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [newNames.length]);

  useEffect(() => {
    if (!field) return;
    setPageLoading(true);
    adminConfigService
      .getAll(field.apiEndpoint)
      .then((res) => setItems(res.data))
      .catch(() => setApiError("Failed to load values."))
      .finally(() => setPageLoading(false));
  }, [field?.apiEndpoint]); // eslint-disable-line

  useEffect(() => {
    if (editingId !== null) editInputRef.current?.focus();
  }, [editingId]);

  // ── Inline edit ───────────────────────────────────────────────────────────
  const startEdit = (item: ConfigItem) => {
    if (isAnyBusy) return;
    setEditingId(item.id);
    setEditingName(item.name);
    setEditError("");
    setApiError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditError("");
  };

  const commitEdit = async () => {
    const name = editingName.trim();
    if (!name) { setEditError("Name cannot be empty."); return; }
    if (editingId === null) return;
    setUpdating(editingId);
    setApiError("");
    try {
      const res = await adminConfigService.update(field.apiEndpoint, editingId, name);
      setItems((prev) => prev.map((i) => (i.id === editingId ? res.data : i)));
      setEditingId(null);
    } catch {
      setApiError("Failed to update value.");
    } finally {
      setUpdating(null);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitEdit();
    if (e.key === "Escape") cancelEdit();
  };

  // ── Add ───────────────────────────────────────────────────────────────────
  const updateNewName = (index: number, value: string) => {
    setNewNames((prev) => prev.map((n, i) => (i === index ? value : n)));
    setNewNameErrors((prev) => prev.map((e, i) => (i === index ? "" : e)));
  };

  const addRow = () => {
    if (isAnyBusy || !!editingId) return;
    if (newNames.length > 0 && !newNames[newNames.length - 1].trim()) {
      setNewNameErrors((prev) =>
        prev.map((e, i) => (i === prev.length - 1 ? "Name is required." : e))
      );
      return;
    }
    setNewNames((prev) => [...prev, ""]);
    setNewNameErrors((prev) => [...prev, ""]);
  };

  const removeRow = (index: number) => {
    setNewNames((prev) => prev.filter((_, i) => i !== index));
    setNewNameErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const discardPending = () => {
    setNewNames([]);
    setNewNameErrors([]);
  };

  const handleAdd = async () => {
    const trimmed = newNames.map((n) => n.trim());
    const errors = trimmed.map((n) => (n ? "" : "Name is required."));
    if (errors.some(Boolean)) { setNewNameErrors(errors); return; }
    setAdding(true);
    setNewNameErrors(trimmed.map(() => ""));
    setApiError("");
    try {
      const res = await adminConfigService.add(field.apiEndpoint, trimmed);
      setItems((prev) => [...prev, ...res.data]);
      setNewNames([]);
      setNewNameErrors([]);
    } catch {
      setApiError("Failed to add value.");
    } finally {
      setAdding(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    setDeleting(id);
    setApiError("");
    try {
      await adminConfigService.remove(field.apiEndpoint, id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      setApiError("Failed to delete value.");
    } finally {
      setDeleting(null);
    }
  };

  const lastRowFilled = newNames.length === 0 || !!newNames[newNames.length - 1].trim();

  // Check if there are any typed changes in the new rows
  const hasNewChanges = newNames.some((n) => n.trim() !== "");

  // ── Row shared heights ────────────────────────────────────────────────────
  // All cells in every row use the same slot heights so rows never jump.
  // input/text slot = 42 px, error slot = 16 px (h-4) + 2 px gap (mt-0.5)
  const ROW_H = "h-[42px]";   // content slot
  const ERR_H = "h-4 mt-0.5"; // error slot

  return (
    /*
      h-full fills the layout wrapper exactly — no margin/extra height that
      would cause the parent container to overflow and scroll.
      overflow-hidden prevents any child from punching through it.
    */
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Page header (fixed, never scrolls) ────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 pt-2 pb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {field.label}
            </h1>

          </div>
        </div>

        <div className="flex items-center gap-2 mr-4 mt-1">
          <HeaderButtons
            size="small"
            onClick={addRow}
            disabled={isAnyBusy || !!editingId || !lastRowFilled}
            icon={<Plus size={13} />}
          >
            Add {field.label}
          </HeaderButtons>
        </div>
      </div>

      {/*
        Card — fills all remaining height via flex-1.
        overflow-hidden clips children to rounded corners and
        prevents any internal size from escaping the card boundary.
        The only element that scrolls is InlineTable (flex-1 min-h-0 overflow-y-auto).
      */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800  shadow-sm">

        {/* Surface API errors (add/edit/delete/load failures) to the user. */}
        {apiError && (
          <div className="flex-shrink-0 mx-5 mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-error text-sm font-medium">
            {apiError}
          </div>
        )}

        {/*
          InlineTable is the ONLY scrollable region.
          - flex-1 min-h-0: takes up all remaining card height
          - overflow-y-auto: vertical scroll when rows exceed the area
          - overflow-x-hidden: no horizontal scroll (table fits the card)
          The className prop is appended to InlineTable's inner container div.
        */}
        <InlineTable
          columns={COLUMNS}
          loading={pageLoading}
          emptyText="No entries yet"
          emptySubText={`Click "Add ${field.label}" above to create the first entry`}
          minWidth={0}
          className="overflow-x-hidden px-5"
        >
          {/* ── Saved rows ─────────────────────────────────────────── */}
          {items.map((item) => {
            const isEditing = editingId === item.id;
            const isUpdating = updating === item.id;
            const isDeleting = deleting === item.id;
            const otherBusy = isAnyBusy && !isEditing && !isUpdating && !isDeleting;

            // Check if the edited value is different from the original value
            const isEditDirty = isEditing && editingName !== item.name;

            return (
              <tr
                key={item.id}
                className={`align-top transition-colors ${isEditing
                  ? "bg-blue-50/70 dark:bg-blue-900/15"
                  : otherBusy
                    ? "opacity-40 pointer-events-none"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  }`}
              >
                {/* Name */}
                <td className="px-3 py-2.5">
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => { setEditingName(e.target.value); setEditError(""); }}
                      onKeyDown={handleEditKeyDown}
                      disabled={isUpdating}
                      className={fieldCls(!!editError)}
                    />
                  ) : (
                    <div className={`flex items-center ${ROW_H} min-w-0`}>
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 capitalize truncate">
                        {item.name}
                      </span>
                    </div>
                  )}
                  <div className={ERR_H}>
                    {isEditing && editError && (
                      <p className="text-xs font-medium leading-tight text-error">{editError}</p>
                    )}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-3 py-2.5">
                  <div className={`flex items-center justify-end gap-1 ${ROW_H}`}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={commitEdit}
                          // Disable if updating OR if no changes have been made to the name
                          disabled={isUpdating || !isEditDirty}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white disabled:opacity-50 transition-colors"
                          aria-label="Save"
                        >
                          {isUpdating
                            ? <Spinner className="h-3.5 w-3.5 text-white" />
                            : <Check size={14} />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={isUpdating}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                          aria-label="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(item)}
                          disabled={isAnyBusy}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Pencil size={11} />
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          disabled={isAnyBusy}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isDeleting
                            ? <Spinner className="h-3 w-3 text-red-500" />
                            : <Trash2 size={11} />}
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                  <div className={ERR_H} />
                </td>
              </tr>
            );
          })}

          {/* ── Pending new rows ────────────────────────────────────── */}
          {newNames.map((name, idx) => (
            <tr
              key={`new-${idx}`}
              className="align-top border-l-2 border-blue-400 dark:border-blue-500 bg-blue-50/30 dark:bg-blue-900/5"
            >
              {/* Name input */}
              <td className="px-3 py-2.5">
                <input
                  ref={idx === newNames.length - 1 ? lastNewInputRef : undefined}
                  type="text"
                  placeholder={`Enter ${field.label.toLowerCase()} name`}
                  value={name}
                  onChange={(e) => updateNewName(idx, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isAnyBusy && !editingId && idx === newNames.length - 1) {
                      handleAdd();
                    }
                  }}
                  disabled={isAnyBusy || !!editingId}
                  className={fieldCls(!!newNameErrors[idx])}
                />
                <div className={ERR_H}>
                  {newNameErrors[idx] && (
                    <p className="text-xs font-medium text-error leading-tight">{newNameErrors[idx]}</p>
                  )}
                </div>
              </td>

              {/* Remove */}
              <td className="px-3 py-2.5">
                <div className={`flex items-center justify-end ${ROW_H}`}>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    disabled={isAnyBusy || !!editingId}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-red-500 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Remove row"
                  >
                    <Minus size={13} />
                  </button>
                </div>
                <div className={ERR_H} />
              </td>
            </tr>
          ))}
        </InlineTable>

        {/* ── Footer — only when there are pending rows (flex-shrink-0) ── */}
        {newNames.length > 0 && (
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/80">
            <button
              type="button"
              onClick={discardPending}
              disabled={adding}
              className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Discard
            </button>
            <HeaderButtons
              onClick={handleAdd}
              loading={adding}
              // Disable if busy OR editing OR if no typed changes have been made
              disabled={isAnyBusy || !!editingId || !hasNewChanges}
              icon={<Check size={13} />}
            >
              Save {newNames.length > 1 ? `${newNames.length} Values` : "Value"}
            </HeaderButtons>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {deleteTarget && (
        <GenericModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          header={`Delete ${field.label}`}
        >
          <DeletePopup
            selectedRow={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            handleDelete={(id) => {
              setDeleteTarget(null);
              handleDelete(id);
            }}
            message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          />
        </GenericModal>
      )}
    </div>
  );
};

/**
 * Route entry for /admin/admin-config/:fieldKey.
 *
 * Resolves the config field from the URL slug, then delegates to the right
 * editor. Keeping the branch in a thin wrapper (that only calls useParams)
 * ensures each editor owns its own hooks — the variant editors are never
 * rendered/unrendered mid-instance, so the rules of hooks are respected.
 */
const ConfigDetail: React.FC = () => {
  const { fieldKey } = useParams<{ fieldKey: string }>();
  const navigate = useNavigate();

  const field = CONFIG_SECTIONS.flatMap((s) => s.fields).find(
    (f) => f.apiEndpoint === `api/${fieldKey}`
  );

  if (!field) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3 text-gray-400">
        <p className="text-lg font-medium">Configuration not found</p>
        <button
          onClick={() => navigate("/admin/admin-config")}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back to configuration
        </button>
      </div>
    );
  }

  // Leave notification recipients use a dedicated admin-email picker.
  if (field.variant === "recipients") {
    return <LeaveRecipientConfig field={field} />;
  }

  return <GenericConfigDetail field={field} />;
};

export default ConfigDetail;